import React, {useEffect, useState, useRef} from "react";
import {observer} from "mobx-react";
import {Dialog} from '@mui/material';
import DraggableInput from '../../Shared/DraggableInput';
import {responseEnum} from '../../../serverClientShared/sharedEnums';
import {useStores} from "../../../Hooks";
import {ProcedureRequester} from '../../../MobxStores/RequestApi/ProcedureRequester';
import ConditionalComponent from "../../Shared/ConditionalComponent";
import SaveFileHook from "../../../Hooks/SaveFileHook";
import ProgressCircleWithCancelAndSuccess from "../../Generic/ProgressCircleWithCancelAndSuccess";
import GetInfoFromUrlHook from "../../../Hooks/GetInfoFromUrlHook";
import ShowErrorModalStoreHook from "../../../Hooks/ShowErrorModalStoreHook";
import {renderAdditionalInformation} from "./AdditionalInformation";

const UploadProcedureGuideDialog = observer(() => {
    const {modalStore, generalStore, projectStore, procedureStore, dragStore, guidesUploadStore} = useStores();
    const saveFile = SaveFileHook()
    const getInfoFromUrl = GetInfoFromUrlHook()
    const ShowErrorModalStore = ShowErrorModalStoreHook()
    const [dragging, setDragging] = useState(false);
    const canceledFilesBeforeUploading = useRef([])

    function closeModal() {
        if (guidesUploadStore.startUploading || guidesUploadStore.doneUploading || guidesUploadStore.beforeUploading) {
            projectStore.mediaLibraryPathArray = [{
                folderId: 'root',
                folderName: generalStore.translate('root')
            }];
            modalStore.isUploadProcedureModalOpen = false;
        }
    }

    async function handleCancelButton(i) {

        guidesUploadStore.progress[i] = 101

        if (!guidesUploadStore.startUploading) {
            canceledFilesBeforeUploading.current = [...canceledFilesBeforeUploading.current, i]
            return;
        }
        const {db, projectId, parentFolder} = getInfoFromUrl()
        const isDeleteBatch = !guidesUploadStore.progress.some(p => p !== 101)
        const result = await ProcedureRequester.cancelConvertGuideProcess(db, [guidesUploadStore.associatedFiles[i]], isDeleteBatch)
        checkIfDoneUploading(guidesUploadStore.progress, [])

    }

    async function onUploadProcedureSuccess() {
        await projectStore.refreshProjectDetails()
        setDragging(false);
        guidesUploadStore.procedureToUpload = []
        guidesUploadStore.progress = [];
        guidesUploadStore.statusArray = []
        guidesUploadStore.isLoading = false;
        guidesUploadStore.doneUploading = false;
        guidesUploadStore.associatedFiles = [];
        guidesUploadStore.startUploading = false;
        guidesUploadStore.procedureHovered = [];
        guidesUploadStore.associatedFiles = [];
        canceledFilesBeforeUploading.current = []
        closeModal()
        generalStore.isProgressLoaderLinear = true
    }

    const filterFiles = (file) => {
        try {
            if (!file.name.includes(".guide")) {
                throw ({response: {status: responseEnum.NO_PERMISSION}})
            } else if (file.size > 2 * 1024 * 1024 * 1024) {
                throw ({response: {status: responseEnum.PAYLOAD_TOO_LARGE}})
            } else {
                return true
            }
        } catch (e) {
            ShowErrorModalStore(e)
            return false
        }
    }
    const handleFilesUpload = async (files) => {

        files = Array.from(files);
        files = files.filter(file => filterFiles(file)).slice(0, 50);
        if (files.length === 0) return;
        generalStore.isProgressLoaderLinear = false
        guidesUploadStore.procedureToUpload = files;
        modalStore.isUploadProcedureModalOpen = false
        guidesUploadStore.isLoading = true;
        guidesUploadStore.beforeUploading = false
        generalStore.isBlockGeneralLinearModal = true
        let result
        if (files.length > 0) {
            try {
                guidesUploadStore.progress = new Array(files.length).fill(0);
                guidesUploadStore.setProgress()
                guidesUploadStore.startUploading = true
                guidesUploadStore.procedureHovered = new Array(files.length).fill(false)
                guidesUploadStore.statusArray = new Array(files.length).fill('Processing files');
                const {db, projectId, parentFolder} = getInfoFromUrl()
                const associatedLinks = await saveFile(files, null, 0);
                guidesUploadStore.associatedFiles = associatedLinks.map(file => file.FileId);
                guidesUploadStore.progress = guidesUploadStore.progress.map(value => value !== 101 ? 10 : 101)
                result = await ProcedureRequester.saveGuidesFile(db, associatedLinks, projectId, parentFolder)
                const canceledFiles = canceledFilesBeforeUploading.current.map(index => associatedLinks[index]?.FileId)
                if (canceledFiles.length > 0) {
                    await ProcedureRequester.cancelConvertGuideProcess(db, canceledFiles, false)
                }
                guidesUploadStore.setProgress()
                if (canceledFilesBeforeUploading.current.length === files.length) {
                    guidesUploadStore.doneUploading = true
                    return;
                }
            } catch (e) {
                modalStore.showErrorMessage(`Error uploading files.`)
                if(!result){
                    guidesUploadStore.resetValues()
                }
            } finally {
                generalStore.isProgressLoaderLinear = true
                modalStore.isUploadProcedureModalOpen = false
                generalStore.isBlockGeneralLinearModal = false
            }
        }

    };

    const checkIfDoneUploading = (progressArray, isCanceledArray) => {
        if (progressArray.length === 0) return false;
        const allDone = progressArray.every((progress, i) =>
            progress >= 100 || isCanceledArray[i]
        );

        if (allDone) {
            guidesUploadStore.doneUploading = true;
        }

        return allDone;

    };

    useEffect(() => {
        const {projectId, parentFolder, db} = getInfoFromUrl()
        if (!guidesUploadStore.startUploading || guidesUploadStore.doneUploading || !db || !projectId) return;
        const interval = setInterval(async () => {
            await guidesUploadStore.updateProgress();
        }, 15000);

        return () => clearInterval(interval);
    }, [guidesUploadStore.startUploading, guidesUploadStore.doneUploading]);

    useEffect(() => {
        if (projectStore.currentProjectId) {
            guidesUploadStore.getInitData(getInfoFromUrl())
        }
    }, [projectStore.currentProjectId]);
    useEffect(() => {
        if (modalStore.isUploadProcedureModalOpen) {
            guidesUploadStore.checkPermission()
        }
    },[modalStore.isUploadProcedureModalOpen])
    return (
        <Dialog
            className={'dialog big-dialog h-' + (dragging ? ' dragging' : '')}
            open={modalStore.isUploadProcedureModalOpen && !guidesUploadStore.isCheckingPermissions}
            onClose={closeModal}
            PaperProps={{
                style: {
                    width: guidesUploadStore.isLoading ? '450px' : '600px',
                    height: '450px',
                    borderRadius: '10px',
                    fontFamily: 'Open Sans',
                },
            }}
        >
            {modalStore.isUploadProcedureModalOpen ? (
                <div className="flex flex-column items-center w-full h-full text-main-normal">
                    <ConditionalComponent if={!guidesUploadStore.isLoading}>
                        <div style={{
                            marginTop: "25px",
                            fontWeight: "700",
                            fontSize: "24px",
                            color: 'var(--main-normal-color)',
                            display: "flex",
                            justifyContent: "center"
                        }}>IMPORT FROM FILE
                        </div>
                        <DraggableInput
                            onInputSelected={handleFilesUpload}
                            onDragStart={() => setDragging(true)}
                            onDragStop={() => setDragging(false)}
                            inputFileTypes=".guide"
                            inputMaxFiles={50}
                            textHeight='h-[50%]'
                            additionalInformation={renderAdditionalInformation()}
                            style='guides'
                        />
                    </ConditionalComponent>


                    <ConditionalComponent if={guidesUploadStore.isLoading}>
                        <div style={{
                            marginTop: "25px",
                            fontWeight: "700",
                            fontSize: "24px",
                            display: "flex",
                            justifyContent: "center",
                        }}>Migrating files
                        </div>
                        <div className="text-main-normal text-xs mt-6">The relevant procedures are still processing.
                            Please keep this open.
                        </div>
                        <div className="absolute top-28 ml-4 overflow-y-auto" style={{maxHeight: "280px"}}>
                            {guidesUploadStore.procedureToUpload.map((file, i) => {
                                return (
                                    <div className="flex flex-row justify-between my-3" key={file.name} style={{width: '350px'}}>
                                        <div style={{ width: '195px' }}
                                             className="text-xs mt-1 rounded-button flex flex-row border-main-normal justify-between">
                                            <div className="line-clamp-2" style={{ maxWidth: '100px' }}>{file.name}</div>
                                            <div className="shrink-0">{guidesUploadStore.statusArray[i]}</div>
                                        </div>
                                        <ProgressCircleWithCancelAndSuccess
                                            value={guidesUploadStore.progress[i]}
                                            i={i}
                                            handleCancelButton={handleCancelButton}
                                            hovered={guidesUploadStore.procedureHovered[i]}
                                            file={file}
                                        />

                                    </div>
                                )
                            })}
                        </div>
                    </ConditionalComponent>

                </div>) : <></>}
        </Dialog>
    );
});

export default UploadProcedureGuideDialog;
